package com.yet.tetris.feature.game.store

import com.arkivanov.mvikotlin.core.store.Store
import com.yet.tetris.domain.model.effects.VisualEffectFeed
import com.yet.tetris.domain.model.game.GameState
import com.yet.tetris.domain.model.settings.GameSettings

internal interface GameStore : Store<GameStore.Intent, GameStore.State, GameStore.Label> {
    data class State(
        val gameState: GameState? = null,
        val settings: GameSettings = GameSettings(),
        val elapsedTime: Long = 0, // milliseconds
        val isLoading: Boolean = false,
        val ghostPieceY: Int? = null,
        val comboStreak: Int = 0,
        val visualEffectFeed: VisualEffectFeed = VisualEffectFeed(),
    ) {
        val isPaused: Boolean
            get() = gameState?.isPaused == true
    }

    sealed class Intent {
        data object PauseGame : Intent()

        data object ResumeGame : Intent()

        data object QuitGame : Intent()

        data object RetryGame : Intent()

        data object MoveLeft : Intent()

        data object MoveRight : Intent()

        data object MoveDown : Intent()

        data object Rotate : Intent()

        data object RotateClockwise : Intent()

        data object RotateCounterClockwise : Intent()

        data object Rotate180 : Intent()

        data object HardDrop : Intent()

        data object Hold : Intent()

        data class HandleSwipe(
            val deltaX: Float,
            val deltaY: Float,
            val velocityX: Float,
            val velocityY: Float,
        ) : Intent()

        data class OnBoardSizeChanged(
            val height: Float,
        ) : Intent()

        data object DragStarted : Intent()

        data class Dragged(
            val deltaX: Float,
            val deltaY: Float,
        ) : Intent()

        data object DragEnded : Intent()

        class ToggleMusic(
            val enabled: Boolean,
        ) : Intent()

        data class VisualEffectConsumed(
            val sequence: Long,
        ) : Intent()
    }

    sealed interface Action {
        data class GameLoadStarted(
            val forceNewGame: Boolean,
        ) : Action
    }

    sealed class Msg {
        data class GameInitialized(
            val gameState: GameState,
            val settings: GameSettings,
        ) : Msg()

        data class GameStateUpdated(
            val gameState: GameState,
            val ghostPieceY: Int?,
        ) : Msg()

        data class PausedChanged(
            val isPaused: Boolean,
        ) : Msg()

        data class ElapsedTimeUpdated(
            val elapsedTime: Long,
        ) : Msg()

        data class LoadingChanged(
            val isLoading: Boolean,
        ) : Msg()

        data class SettingsUpdated(
            val settings: GameSettings,
        ) : Msg()

        data class ComboStreakUpdated(
            val comboStreak: Int,
        ) : Msg()

        data class VisualEffectFeedUpdated(
            val comboStreak: Int,
            val visualEffectFeed: VisualEffectFeed,
        ) : Msg()

        data class VisualEffectConsumed(
            val sequence: Long,
        ) : Msg()
    }

    sealed class Label {
        data class ShowError(
            val message: String,
        ) : Label()

        data object GamePaused : Label()

        data object GameOver : Label()

        data object NavigateBack : Label()

        data object ResumeGame : Label()
    }
}
